import { styled } from "styled-components";

import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";
import { ChangeCell, ProgressBarWithValueCell, TextCell } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes, colorsSets, rgba } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter, timeFilter } from "app/filters/ngFilters";
import type { IDurationData } from "app/services/DurationService";

import type { KeySizingComparisonRow } from "./types";

const Bullet = styled(Box)<{ $color: string }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) => props.$color || colorsSets.c["midnight 300"]};
`;

const InlineHeaderCell = styled.div`
    display: inline-block;
`;

const stripedBackground = `repeating-linear-gradient(
    -45deg,
    ${colorsPalettes.carbon[200]} 0,
    ${colorsPalettes.carbon[200]} 1px,
    transparent 1px,
    transparent 3px
)`;

const MetricHeaderWithBullet = ({ title, isPreviousPeriod }: { title: string; isPreviousPeriod: boolean }) => (
    <InlineHeaderCell>
        <Flex
            alignItems="center"
            gap="4px"
        >
            <Box
                width="8px"
                height="8px"
                borderRadius="50%"
                background={isPreviousPeriod ? stripedBackground : colorsPalettes.carbon[200]}
            />
            <Text fontVariant="captionTitle">{title}</Text>
        </Flex>
    </InlineHeaderCell>
);

const EmptyColumn = { width: 12 };

const formatters = {
    percentage: percentageSignFilter(),
    abbrNumber: abbrNumberFilter(),
    time: timeFilter(),
};

const getStripedBackground = (color: string) => {
    return `${color} repeating-linear-gradient(
    -45deg,
    ${rgba(colorsPalettes.carbon[0], 0.75)} 0,
    ${rgba(colorsPalettes.carbon[0], 0.75)} 2px,
    transparent 2px,
    transparent 4px
  )`;
};

/**
 * Normalize `IDurationData['forWidget']` into an explicit shape.
 * Returns `{ current?, previous? }` where values are plain strings or undefined.
 */
const normalizeDurationForWidget = (
    durationForWidget: IDurationData["forWidget"],
): { current?: string; previous?: string } => {
    if (!durationForWidget) {
        return { current: undefined };
    }

    if (Array.isArray(durationForWidget)) {
        return { current: durationForWidget[0], previous: durationForWidget[1] };
    }

    return { current: durationForWidget };
};

export const getColumnConfig: (
    isPeriodOverPeriod: boolean,
    isYearOverYear: boolean,
    durationForWidget: IDurationData["forWidget"],
    hasSelectedCategoryWithSegments: boolean,
) => ColumnsType<KeySizingComparisonRow> = (
    isPeriodOverPeriod,
    isYearOverYear,
    durationForWidget,
    hasSelectedCategoryWithSegments,
) => {
    const normalizedDuration = normalizeDurationForWidget(durationForWidget);

    return [
        EmptyColumn,
        {
            dataIndex: "categoryName",
            title: translate("market.research.overview.comparison.keysizing.table.category"),
            minWidth: 255,
            render: (categoryName: KeySizingComparisonRow["categoryName"], row) => (
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Bullet $color={row.color} />
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {categoryName}
                    </Text>
                </Flex>
            ),
        },
        ...(isPeriodOverPeriod
            ? [
                  {
                      dataIndex: "previousTotalVisits",
                      title: translate("market.research.overview.comparison.keysizing.table.total.visits", {
                          duration:
                              isPeriodOverPeriod && normalizedDuration.previous
                                  ? `(${normalizedDuration.previous})`
                                  : null,
                      }),
                      minWidth: 255,
                      render: (previousTotalVisits, row) => (
                          <ProgressBarWithValueCell
                              share={previousTotalVisits?.share ?? 0}
                              valueMinWidth="48px"
                              color={getStripedBackground(row.color) as StrictColor}
                              value={formatters.abbrNumber(previousTotalVisits?.value ?? 0)}
                          />
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        {
            dataIndex: "totalVisits",
            title: translate("market.research.overview.comparison.keysizing.table.total.visits", {
                duration: isPeriodOverPeriod && normalizedDuration.current ? `(${normalizedDuration.current})` : null,
            }),
            minWidth: 255,
            render: ({ value, share }: KeySizingComparisonRow["totalVisits"], row) => (
                <ProgressBarWithValueCell
                    share={share}
                    valueMinWidth="48px"
                    color={row.color as StrictColor}
                    value={formatters.abbrNumber(value)}
                />
            ),
        },
        {
            dataIndex: "periodChange",
            title: translate(
                isPeriodOverPeriod && isYearOverYear
                    ? "market.research.overview.comparison.keysizing.table.yoy.change"
                    : isPeriodOverPeriod
                      ? "market.research.overview.comparison.keysizing.table.pop.change"
                      : "market.research.overview.comparison.keysizing.table.change",
            ),
            width: 90,
            align: "right",
            render: (periodChange: KeySizingComparisonRow["periodChange"]) => (
                <Flex justifyContent="flex-end">
                    <ChangeCell
                        value={periodChange}
                        formattedValue={formatters.percentage(periodChange, 2) as string}
                    />
                </Flex>
            ),
        },
        ...(isPeriodOverPeriod && !hasSelectedCategoryWithSegments
            ? [
                  {
                      dataIndex: "previousVisitsPerVisitor",
                      title: (
                          <MetricHeaderWithBullet
                              title={translate(
                                  "market.research.overview.comparison.keysizing.table.visits.per.visitor",
                              )}
                              isPreviousPeriod={true}
                          />
                      ),
                      width: 112,
                      align: "right",
                      render: (visits) => (
                          <TextCell>
                              {
                                  // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                                  visits !== undefined ? formatters.abbrNumber(visits, 0) : "-"
                              }
                          </TextCell>
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        ...(!hasSelectedCategoryWithSegments
            ? [
                  {
                      dataIndex: "visitsPerVisitor",
                      title: isPeriodOverPeriod ? (
                          <MetricHeaderWithBullet
                              title={translate(
                                  "market.research.overview.comparison.keysizing.table.visits.per.visitor",
                              )}
                              isPreviousPeriod={false}
                          />
                      ) : (
                          translate("market.research.overview.comparison.keysizing.table.visits.per.visitor")
                      ),
                      width: 112,
                      align: "right",
                      render: (visits: KeySizingComparisonRow["visitsPerVisitor"]) => (
                          <TextCell>
                              {
                                  // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                                  formatters.abbrNumber(visits, 0)
                              }
                          </TextCell>
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        ...(isPeriodOverPeriod
            ? [
                  {
                      dataIndex: "previousUnbouncedVisits",
                      title: (
                          <MetricHeaderWithBullet
                              title={translate("market.research.overview.comparison.keysizing.table.unbounced.visits")}
                              isPreviousPeriod={true}
                          />
                      ),
                      align: "right",
                      width: 120,
                      render: (unbouncedVisits) => (
                          <TextCell>
                              {
                                  // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                                  unbouncedVisits !== undefined ? formatters.abbrNumber(unbouncedVisits, 0) : "-"
                              }
                          </TextCell>
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        {
            dataIndex: "unbouncedVisits",
            title: isPeriodOverPeriod ? (
                <MetricHeaderWithBullet
                    title={translate("market.research.overview.comparison.keysizing.table.unbounced.visits")}
                    isPreviousPeriod={false}
                />
            ) : (
                translate("market.research.overview.comparison.keysizing.table.unbounced.visits")
            ),
            align: "right",
            width: 120,
            render: (unbouncedVisits: KeySizingComparisonRow["unbouncedVisits"]) => (
                <TextCell>
                    {
                        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                        formatters.abbrNumber(unbouncedVisits, 0)
                    }
                </TextCell>
            ),
        },
        ...(isPeriodOverPeriod
            ? [
                  {
                      dataIndex: "previousPagesPerVisit",
                      title: (
                          <MetricHeaderWithBullet
                              title={translate("market.research.overview.comparison.keysizing.table.pages.per.visit")}
                              isPreviousPeriod={true}
                          />
                      ),
                      width: 105,
                      align: "right",
                      render: (pages) => (
                          <TextCell>
                              {
                                  // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                                  pages !== undefined ? formatters.abbrNumber(pages, 0) : "-"
                              }
                          </TextCell>
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        {
            dataIndex: "pagesPerVisit",
            title: isPeriodOverPeriod ? (
                <MetricHeaderWithBullet
                    title={translate("market.research.overview.comparison.keysizing.table.pages.per.visit")}
                    isPreviousPeriod={false}
                />
            ) : (
                translate("market.research.overview.comparison.keysizing.table.pages.per.visit")
            ),
            width: 105,
            align: "right",
            render: (pages: KeySizingComparisonRow["pagesPerVisit"]) => (
                <TextCell>
                    {
                        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                        formatters.abbrNumber(pages, 0)
                    }
                </TextCell>
            ),
        },
        ...(isPeriodOverPeriod
            ? [
                  {
                      dataIndex: "previousVisitDuration",
                      title: (
                          <MetricHeaderWithBullet
                              title={translate("market.research.overview.comparison.keysizing.table.visit.duration")}
                              isPreviousPeriod={true}
                          />
                      ),
                      align: "right",
                      width: 105,
                      render: (duration) => (
                          <TextCell>{duration !== undefined ? formatters.time(duration, null) : "-"}</TextCell>
                      ),
                  } satisfies ColumnType<KeySizingComparisonRow>,
              ]
            : []),
        {
            dataIndex: "visitDuration",
            title: isPeriodOverPeriod ? (
                <MetricHeaderWithBullet
                    title={translate("market.research.overview.comparison.keysizing.table.visit.duration")}
                    isPreviousPeriod={false}
                />
            ) : (
                translate("market.research.overview.comparison.keysizing.table.visit.duration")
            ),
            align: "right",
            width: 105,
            render: (duration: KeySizingComparisonRow["visitDuration"]) => (
                <TextCell>{formatters.time(duration, null)}</TextCell>
            ),
        },
        EmptyColumn,
    ];
};
